import { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Select, MenuItem, FormControl, InputLabel, Container, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { Template } from '../types';

export default function TemplatesPage() {
  const [templates, setTemplates] = useLocalStorage<Template[]>('templates', []);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [isNew, setIsNew] = useState(false);
  const [formData, setFormData] = useState<Template>({ name: '', prompt: '' });

  useEffect(() => {
    if (templates.length === 0) {
      setSelectedTemplate('new');
      setIsNew(true);
    }
  }, [templates]);

  const handleTemplateSelect = (templateName: string) => {
    if (templateName === 'new') {
      setIsNew(true);
      setFormData({ name: '', prompt: '' });
    } else {
      setIsNew(false);
      const template = templates.find(t => t.name === templateName);
      if (template) {
        setFormData(template);
      }
    }
    setSelectedTemplate(templateName);
  };

  const handleSave = () => {
    if (!formData.name || !formData.prompt) return;

    const newTemplates = [...templates];
    const existingIndex = newTemplates.findIndex(t => t.name === formData.name);
    const oldTemplateIndex = newTemplates.findIndex(t => t.name === selectedTemplate);

    if (existingIndex >= 0 && existingIndex !== oldTemplateIndex) {
      // If trying to rename to a name that already exists (and it's not the current template)
      return;
    }

    if (oldTemplateIndex >= 0) {
      // If we're editing an existing template
      newTemplates[oldTemplateIndex] = formData;
    } else {
      // If we're creating a new template
      newTemplates.push(formData);
    }

    setTemplates(newTemplates);
    setIsNew(false);
    setSelectedTemplate(formData.name);
  };

  const handleDelete = () => {
    if (!selectedTemplate || selectedTemplate === 'new') return;

    const newTemplates = templates.filter(t => t.name !== selectedTemplate);
    setTemplates(newTemplates);
    setSelectedTemplate('');
    setFormData({ name: '', prompt: '' });
  };

  return (
    <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        pt: 2
      }}>
        <Typography variant="h5">Templates</Typography>
        {(selectedTemplate || isNew) && (
          <IconButton
            color="primary"
            onClick={handleSave}
            disabled={!formData.name || !formData.prompt}
            title={!formData.name || !formData.prompt ? "Fill in name and prompt to save" : "Save template"}
          >
            <SaveIcon />
          </IconButton>
        )}
      </Box>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Select Template</InputLabel>
        <Select
          value={selectedTemplate}
          label="Select Template"
          onChange={(e) => handleTemplateSelect(e.target.value)}
        >
          <MenuItem value="new">New Template</MenuItem>
          {templates.map((template) => (
            <MenuItem key={template.name} value={template.name}>
              {template.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {(selectedTemplate || isNew) && (
        <Box>
          <TextField
            fullWidth
            label="Template Name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Prompt"
            value={formData.prompt}
            onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
            margin="normal"
            multiline
            rows={4}
          />

          {!isNew && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDelete}
              >
                Delete
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
} 